/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Button, FlexRow, FlexCol, NumberField } from 'sarsaparilla';
import get from 'lodash/get';
import { Gnoment } from 'shared-ui/gnoment';
import { NoQuotaMods, hasRule, issuanceStatuses } from '../../utils/utilsNew';
import {
    REDUCIBLE_GROUP_SIZE_KEY_NAME,
    ENCHANTMENT_ID,
    ENCHANTMENT_CORE_DIVISION_ID,
    PERMIT_COMPLETE,
    SAN_JUAN_ID,
} from '../../constants';

const propTypes = {
    groupMembers: PropTypes.arrayOf(PropTypes.shape({})),
    handleGroupMemberChange: PropTypes.func.isRequired,
    minMemberQuota: PropTypes.number,
    maxMemberQuota: PropTypes.number,
    isDisabled: PropTypes.bool,
    entryDate: PropTypes.instanceOf(Gnoment),
    hasSecureQuotaText: PropTypes.bool,
    inyoQuotaInstructions: PropTypes.bool,
    isQuotaModifyAllowed: PropTypes.bool,
    issuance: PropTypes.shape({
        selected_lottery_preference: PropTypes.shape({
            group_max_size: PropTypes.number,
        }),
        lottery_accepted: PropTypes.bool,
        lottery_awarded: PropTypes.bool,
    }),
    onModifyDetails: PropTypes.func,
    permit: PropTypes.shape({
        rules: PropTypes.array,
        id: PropTypes.string,
    }),
    modifyDetailsButtonText: PropTypes.string,
};

const defaultProps = {
    groupMembers: [],
    minMemberQuota: 1,
    maxMemberQuota: 100,
};

export default class SimpleGroupMemberList extends React.Component {
    componentDidMount() {
        const minRequired = Math.max(
            this.props.minMemberQuota,
            this.props.groupMembers.length
        );
        if (minRequired && minRequired > this.props.groupMembers.length) {
            this.handleMemberCountChange({ target: { value: minRequired } });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.minMemberQuota &&
            this.props.minMemberQuota !== prevProps.minMemberQuota &&
            this.props.groupMembers?.length < this.props.minMemberQuota
        ) {
            this.handleMemberCountChange({
                target: {
                    value: this.props.minMemberQuota,
                },
            });
        }
    }

    onChangeDetailsClick = () => {
        this.props.onModifyDetails(this.props.issuance);
    };

    getGroupText() {
        const maxGroupSize = this.props.maxMemberQuota;
        const {
            lottery_awarded: lotteryAwarded,
            lottery_accepted: lotteryAccepted,
            status,
        } = get(this.props, 'issuance', '');
        const isLottery =
            (lotteryAwarded || lotteryAccepted) &&
            status !== issuanceStatuses.get(PERMIT_COMPLETE);

        if (this.props.inyoQuotaInstructions) {
            return (
                <p>
                    Indicate how many total people will be in your group, including you.
                    You may decrease this number, but it is set to the max selected in
                    detailed availability.
                </p>
            );
        }
        if (this.props.hasSecureQuotaText) {
            if (this.props.permit.id === SAN_JUAN_ID && isLottery) {
                return (
                    <p>
                        The group size for this permit was determined based on
                        availability and your minimum and maximum group size range in your
                        lottery preferences and you must accept your award prior to making
                        any modifications. Once accepted, you may decrease or increase
                        your group size, if additional spots are available, directly on
                        your reservation.
                    </p>
                );
            }

            return (
                <p>
                    The quota for this permit is based on the number of people going on
                    the trip. It can only be increased if there are enough spots available
                    for the day
                </p>
            );
        }

        if (!maxGroupSize || maxGroupSize >= defaultProps.maxMemberQuota) {
            return (
                <p>
                    Add everyone that will be going with you. This can be changed any time
                    before the permit is issued.
                </p>
            );
        }

        if (hasRule(REDUCIBLE_GROUP_SIZE_KEY_NAME, this.props.permit.rules, '')) {
            return (
                <p>
                    The quota for this permit is based on the number of people going on
                    the trip. It can only be decreased.
                </p>
            );
        }

        if (this.props.permit.id === ENCHANTMENT_ID) {
            return (
                <p>
                    Add everyone that will be going with you. Maximum of {maxGroupSize}{' '}
                    members at this facility.
                </p>
            );
        }

        return (
            <p>
                Add everyone that will be going with you. This can be changed any time
                before the permit is issued. Maximum of {maxGroupSize} members at this
                facility.
            </p>
        );
    }

    handleMemberCountChange = (memberCount) => {
        const current = this.props.groupMembers.length;
        const {
            lottery_awarded: lotteryAwarded,
            lottery_accepted: lotteryAccepted,
            selected_lottery_preference: selectedLP,
            division_id: divisionID,
            status,
        } = get(this.props, 'issuance', '');
        const isLottery =
            (lotteryAwarded || lotteryAccepted) &&
            status !== issuanceStatuses.get(PERMIT_COMPLETE);
        const max =
            this.props.permit.id === ENCHANTMENT_ID &&
            isLottery &&
            divisionID === ENCHANTMENT_CORE_DIVISION_ID
                ? selectedLP.group_max_size
                : this.props.maxMemberQuota;
        let newCount = +memberCount || 0;

        if (max && newCount > max && max < defaultProps.maxMemberQuota) {
            newCount = max;
        }

        if (this.props.minMemberQuota && newCount < this.props.minMemberQuota) {
            newCount = this.props.minMemberQuota;
        }

        if (newCount > current) {
            const newGroupMembers = [...this.props.groupMembers];
            for (let i = current; i < newCount; i++) {
                // require member to have startDate/endDate fee calculations (depending on permit). Absence of either will lead to fee discrepancy.
                const newMember = {
                    startDate: this.props.entryDate,
                    endDate: this.props.entryDate,
                };
                newGroupMembers.push(newMember);
            }
            this.props.handleGroupMemberChange(newGroupMembers);
        } else if (newCount < current) {
            const newGroupMembers = this.props.groupMembers.slice(0, newCount);
            this.props.handleGroupMemberChange(newGroupMembers);
        }
    };

    renderChangeDetails() {
        const {
            lottery_awarded: lotteryAwarded,
            lottery_accepted: lotteryAccepted,
            status,
        } = get(this.props, 'issuance', '');
        const isLottery =
            (lotteryAwarded || lotteryAccepted) &&
            status !== issuanceStatuses.get(PERMIT_COMPLETE);
        if (!this.props.hasSecureQuotaText && !this.props.inyoQuotaInstructions) {
            // if quota isn't determined by group size, we don't display this button or text here.
            return null;
        }

        if (this.props.permit.id === ENCHANTMENT_ID && isLottery) {
            return null;
        }

        if (this.props.permit.id === SAN_JUAN_ID && isLottery) {
            return null;
        }

        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        type="button"
                        appearance="link"
                        className="change-details-link"
                        screenReaderTextBefore="Redirect to detailed availability to modify the group size of current reservation"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.onChangeDetailsClick}
                    >
                        {this.props.modifyDetailsButtonText}
                    </Button>
                </div>
            );
        }
        return NoQuotaMods;
    }

    render() {
        const {
            lottery_awarded: lotteryAwarded,
            lottery_accepted: lotteryAccepted,
            selected_lottery_preference: selectedLP,
            division_id: divisionID,
            status,
        } = get(this.props, 'issuance', '');
        const isLottery =
            (lotteryAwarded || lotteryAccepted) &&
            status !== issuanceStatuses.get(PERMIT_COMPLETE);
        const max =
            this.props.permit.id === ENCHANTMENT_ID &&
            isLottery &&
            divisionID === ENCHANTMENT_CORE_DIVISION_ID
                ? selectedLP.group_max_size
                : this.props.maxMemberQuota;
        const disabled =
            this.props.permit.id === ENCHANTMENT_ID && isLottery
                ? false
                : this.props.isDisabled;
        return (
            <div
                data-testid="simple-group-member-list"
                className="per-group-member-form per-section-group"
            >
                <h2 className="h4">Group Size</h2>
                {this.getGroupText()}
                <FlexRow>
                    <FlexCol md={3}>
                        <div className="rec-form-item-wrap">
                            <NumberField
                                id="memberCountInput"
                                isDisabled={disabled}
                                min={this.props.minMemberQuota}
                                max={max}
                                step={1}
                                units="guests"
                                value={Math.max(
                                    this.props.minMemberQuota,
                                    this.props.groupMembers.length
                                )}
                                onChange={this.handleMemberCountChange}
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
                {this.renderChangeDetails()}
            </div>
        );
    }
}

SimpleGroupMemberList.propTypes = propTypes;
SimpleGroupMemberList.defaultProps = defaultProps;
